import type { CardAppearancePatch, FormationBanners, FormationBannersPatch } from '@/shared';
import { api } from './api';
import type { Product } from './products.service';
import { readAllAdminPages } from './admin-pagination';

// Backwards compatibility alias
type Course = Product;

export interface MemberArea {
  experienceConfigured?: boolean;
  id: string;
  name: string;
  slug: string;
  description?: string;
  logo?: string;
  favicon?: string;
  primaryColor?: string;
  secondaryColor?: string;
  themeColors?: {
    primary?: string;
    secondary?: string;
    background?: string;
    text?: string;
  };
  customCss?: string;
  menuItems?: MenuItem[];
  isActive: boolean;
  banner?: string;
  /** Hierarquia dos produtos: 0 é o mais alto. */
  priority?: number;
  coursesCount?: number;
  accessGroups?: AccessGroupInfo[];
  /**
   * Formações do produto. Na leitura do aluno vêm todas, cada uma com a
   * decisão de acesso (as sem acesso aparecem com cadeado).
   */
  courses?: AreaCourse[];
  /** Só na leitura do aluno: acesso dele ao produto. */
  access?: AreaAccess;
  /** Só na leitura do aluno: o produto mais alto que ele pode abrir. */
  isMain?: boolean;
  /** Título e banner da jornada ("Jornada e ofertas"), usados no banner principal da home. */
  journey?: { title: string; bannerUrl: string | null } | null;
  mainProductId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AreaAccess {
  allowed: boolean;
  reason: 'granted' | 'expired' | 'not_owned';
  expiresAt: string | null;
}

export type AreaCourse = Course & { expiresAt?: string | null };

export interface MenuItem {
  title: string;
  url: string;
  icon?: string;
  openInNewTab?: boolean;
  order?: number;
}

export interface AccessGroupInfo {
  id: string;
  title: string;
  isActive: boolean;
  productsCount: number;
  enrollmentsCount?: number;
  products?: Course[];
}

class MemberAreasService {
  // User methods
  async getMyMemberAreas(): Promise<MemberArea[]> {
    const { data } = await api.get('/member-areas/my');
    return data?.data ?? data;
  }

  async getMemberAreaBySlug(slug: string): Promise<MemberArea> {
    const { data } = await api.get(`/member-areas/slug/${slug}`);
    return data?.data ?? data;
  }

  async getMemberAreaCourses(memberAreaId: string): Promise<Course[]> {
    const { data } = await api.get(`/member-areas/${memberAreaId}/courses`);
    return data?.data ?? data;
  }

  async checkAccess(memberAreaId: string): Promise<{ hasAccess: boolean }> {
    const { data } = await api.get(
      `/member-areas/${memberAreaId}/check-access`,
    );
    return data?.data ?? data;
  }

  // Admin methods
  async findAll(
    params: {
      page?: number;
      limit?: number;
      search?: string;
      isActive?: boolean;
    } = {},
  ): Promise<{
    data: MemberArea[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });

    const { data } = await api.get(
      `/member-areas/admin?${searchParams.toString()}`,
    );
    return data;
  }

  async findById(id: string): Promise<MemberArea> {
    const { data } = await api.get(`/member-areas/admin/${id}`);
    return data?.data ?? data;
  }

  /** Includes inactive areas/groups and draft courses without granting access. */
  async getAllAdminMemberAreas(): Promise<MemberArea[]> {
    const areas = await readAllAdminPages<MemberArea>((page) =>
      this.findAll({ page, limit: 100 }),
    );
    const details: MemberArea[] = [];
    // Bound concurrent detail requests while preserving the server's ordering.
    for (let offset = 0; offset < areas.length; offset += 6) {
      const batch = await Promise.all(
        areas.slice(offset, offset + 6).map(async (area) => {
          const detail = await this.findById(area.id);
          if (
            !detail ||
            detail.id !== area.id ||
            !Array.isArray(detail.accessGroups) ||
            detail.accessGroups.some((group) => !Array.isArray(group.products))
          ) {
            throw new Error('Detalhes da área de membros incompletos.');
          }
          return detail;
        }),
      );
      details.push(...batch);
    }
    return details;
  }

  async create(
    memberArea: Partial<MemberArea> & {
      accessGroupIds?: string[];
    },
  ): Promise<MemberArea> {
    const { data } = await api.post('/member-areas', memberArea);
    return data;
  }

  async update(
    id: string,
    memberArea: Partial<MemberArea> & { accessGroupIds?: string[] },
  ): Promise<MemberArea> {
    const { data } = await api.put(`/member-areas/${id}`, memberArea);
    return data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/member-areas/${id}`);
  }

  /** Todos os produtos (áreas) na ordem da hierarquia, para o admin. */
  async listProducts(): Promise<MemberArea[]> {
    return readAllAdminPages<MemberArea>((page) =>
      this.findAll({ page, limit: 100 }),
    );
  }

  /** Define a hierarquia: o primeiro ID é o produto mais alto. */
  async reorder(ids: string[]): Promise<void> {
    await api.put('/member-areas/reorder', { ids });
  }

  // ==================== BANNERS ====================

  async getBanners(): Promise<Banner[]> {
    const { data } = await api.get('/member-areas/banners/admin');
    return data?.data || data;
  }

  async getPublicBanners(position?: string): Promise<Banner[]> {
    const params = position ? `?position=${position}` : '';
    const { data } = await api.get(`/member-areas/banners/public${params}`);
    return data?.data || data;
  }

  async createBanner(
    banner: Omit<Banner, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<Banner> {
    const { data } = await api.post('/member-areas/banners', banner);
    return data?.data || data;
  }

  async updateBanner(id: string, banner: Partial<Banner>): Promise<Banner> {
    const { data } = await api.put(`/member-areas/banners/${id}`, banner);
    return data?.data || data;
  }

  async deleteBanner(id: string): Promise<void> {
    await api.delete(`/member-areas/banners/${id}`);
  }

  async reorderBanners(ids: string[]): Promise<void> {
    await api.put('/member-areas/banners/reorder', { ids });
  }

  // ==================== MENU ITEMS ====================

  async getMenuItems(memberAreaId?: string): Promise<CustomMenuItem[]> {
    const { data } = await api.get('/member-areas/menus/admin', {
      params: memberAreaId ? { memberAreaId } : undefined,
    });
    return data?.data || data;
  }

  async getPublicMenuItems(memberAreaId?: string): Promise<CustomMenuItem[]> {
    const { data } = await api.get('/member-areas/menus/public', {
      params: memberAreaId ? { memberAreaId } : undefined,
    });
    return data?.data || data;
  }

  async createMenuItem(
    item: Omit<CustomMenuItem, 'id' | 'createdAt' | 'updatedAt'>,
    memberAreaId?: string,
  ): Promise<CustomMenuItem> {
    const { data } = await api.post('/member-areas/menus', { ...item, memberAreaId });
    return data?.data || data;
  }

  async updateMenuItem(
    id: string,
    item: Partial<CustomMenuItem>,
    memberAreaId?: string,
  ): Promise<CustomMenuItem> {
    const { data } = await api.put(`/member-areas/menus/${id}`, item, {
      params: memberAreaId ? { memberAreaId } : undefined,
    });
    return data?.data || data;
  }

  async deleteMenuItem(id: string, memberAreaId?: string): Promise<void> {
    await api.delete(`/member-areas/menus/${id}`, {
      params: memberAreaId ? { memberAreaId } : undefined,
    });
  }

  async reorderMenuItems(ids: string[], memberAreaId?: string): Promise<void> {
    await api.put('/member-areas/menus/reorder', { ids, memberAreaId });
  }

  async applyMenuToAllAreas(source: ApplyMenuToAllAreasInput): Promise<ApplyMenuToAllAreasResult> {
    const { data } = await api.post('/member-areas/menus/apply-to-all', source, {
      retryOnNetworkError: false,
      timeout: 60000,
    });
    return data?.data ?? data;
  }

  // ==================== PLATFORM SETTINGS ====================

  async getPlatformSettings(): Promise<PlatformSettings> {
    const { data } = await api.get('/member-areas/settings/public');
    return data?.data || data;
  }

  async updatePlatformSettings(
    settings: PlatformSettingsUpdate,
  ): Promise<PlatformSettings> {
    const { data } = await api.put('/member-areas/settings', settings);
    return data?.data || data;
  }
}

export interface PlatformSettings {
  cardAppearance?: CardAppearancePatch;
  formationBanners?: FormationBanners;
  whatsappNumber: string;
  whatsappMessage: string;
  whatsappEnabled: boolean;
  emailWebhookUrl: string;
}

export type PlatformSettingsUpdate = Partial<Omit<PlatformSettings, 'formationBanners'>> & {
  formationBanners?: FormationBannersPatch;
};

export interface Banner {
  id: string;
  title?: string;
  imageUrl: string;
  linkUrl?: string;
  position: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ApplyMenuToAllAreasInput {
  sourceMemberAreaId?: string;
  sourceProductId?: string;
}

export interface ApplyMenuToAllAreasResult {
  areasUpdated: number;
  itemsPerArea: number;
}

export interface CustomMenuItem {
  id: string;
  title: string;
  icon?: string;
  url?: string;
  isExternal: boolean;
  openNewTab: boolean;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const memberAreasService = new MemberAreasService();
